import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Download, Users, Clock, Gift, RefreshCw, ChevronDown } from 'lucide-react';
import type { AdminEmployee, EmployeeCategory } from '../types/models';
import type { FulltimePayroll, ParttimePayroll, PayrollTabProps } from './PayrollTypes';
import {
  determineCategory, calculateFulltimePayroll, calculateParttimePayroll,
  getAutoValue,
} from './PayrollCalculationEngine';
import {
  getPartSpecialAllowanceUnitPrice,
  isBenefitRuleEnabledForPeriod,
  isPartSpecialAllowanceEnabledForPeriod,
  PART_SPECIAL_ALLOWANCE_LABEL,
} from './PayrollBenefitRules';
import {
  exportFulltimePayrollCsv, exportParttimePayrollCsv, exportAllPayrollCsv,
} from './PayrollCsvExport';
import { sortEmployeesDefault } from '../lib/employeeSorter';
import FulltimePayrollTable from './components/FulltimePayrollTable';
import ParttimePayrollTable from './components/ParttimePayrollTable';
import ParttimeBenefitTable from './components/ParttimeBenefitTable';
import LaborSubmissionPanel from './labor-export/LaborSubmissionPanel';
import { buildLaborRows } from './labor-export/types';
import { loadPayrollOverrides } from './labor-export/submissionRepository';

type CategoryTab = 'fulltime' | 'parttime' | 'benefit';

/** Compute display period string from selectedMonth + closingDay */
function computePeriodLabel(selectedMonth: string, closingDay: string): string {
  const closing = parseInt(closingDay, 10) || 15;
  const [year, month] = selectedMonth.split('-').map(Number);
  if (!year || !month) return selectedMonth;
  const prevMonth = month === 1 ? 12 : month - 1;
  return `${year}\u5E74${String(month).padStart(2, '0')}\u6708\u5206 (${prevMonth}/${closing + 1}\u301C${month}/${closing})`;
}

const CATEGORY_TABS: { key: CategoryTab; label: string; icon: React.ReactNode }[] = [
  { key: 'fulltime', label: '\u6B63\u8077\u54E1', icon: <Users className="w-4 h-4" /> },
  { key: 'parttime', label: '\u975E\u5E38\u52E4', icon: <Clock className="w-4 h-4" /> },
  { key: 'benefit', label: '\u30D1\u30FC\u30C8\u624B\u5F53\u8A08\u7B97', icon: <Gift className="w-4 h-4" /> },
];

const FULLTIME_FIELDS = ['absence_days', 'late_early_minutes', 'special_benefit', 'special_allowance', 'transport_allowance', 'meal_deduction'];
const FULLTIME_FIELDS_AFTER_BENEFIT_RULE = ['absence_days', 'late_early_minutes', 'special_allowance', 'transport_allowance', 'meal_deduction'];
const PARTTIME_FIELDS = ['absence_days', 'late_early_minutes', 'special_benefit', 'special_allowance', 'transport_allowance', 'meal_deduction'];
const PARTTIME_FIELDS_AFTER_PART_RULE = ['absence_days', 'late_early_minutes', 'special_allowance', 'transport_allowance', 'meal_deduction'];

export const PayrollTab: React.FC<PayrollTabProps> = ({
  employees, monthlyData, selectedMonth, closingDay,
  calculateOvertime, supabaseClient, monthlyLoading, onMonthChange,
  payrollSourceFingerprint, payrollSourceError, onReloadMonthly,
}) => {
  const [activeCategory, setActiveCategory] = useState<CategoryTab>('fulltime');
  const [overrides, setOverrides] = useState<Map<string, Map<string, number>>>(new Map());
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [overridesError, setOverridesError] = useState('');
  const [overridesPeriod, setOverridesPeriod] = useState('');
  const [overridesSaving, setOverridesSaving] = useState(false);
  const [submissionConfirmed, setSubmissionConfirmed] = useState(false);
  const overrideFetchSequence = useRef(0);
  const overrideSaveActive = useRef(false);
  const currentPeriod = useRef(selectedMonth);
  currentPeriod.current = selectedMonth;
  const [csvMenuOpen, setCsvMenuOpen] = useState(false);
  const csvMenuRef = useRef<HTMLDivElement>(null);
  const benefitRuleEnabled = useMemo(
    () => isBenefitRuleEnabledForPeriod(selectedMonth, closingDay),
    [selectedMonth, closingDay]
  );
  const partSpecialAllowanceEnabled = useMemo(
    () => isPartSpecialAllowanceEnabledForPeriod(selectedMonth, closingDay),
    [selectedMonth, closingDay]
  );
  const payrollRuleOptions = useMemo(
    () => ({ benefitRuleEnabled, partSpecialAllowanceEnabled }),
    [benefitRuleEnabled, partSpecialAllowanceEnabled]
  );
  const fulltimeFields = benefitRuleEnabled ? FULLTIME_FIELDS_AFTER_BENEFIT_RULE : FULLTIME_FIELDS;
  const parttimeFields = partSpecialAllowanceEnabled ? PARTTIME_FIELDS_AFTER_PART_RULE : PARTTIME_FIELDS;

  // Close CSV menu on outside click
  useEffect(() => {
    if (!csvMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (csvMenuRef.current && !csvMenuRef.current.contains(e.target as Node)) setCsvMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [csvMenuOpen]);

  // Categorize employees who worked during the payroll period
  // (includes retirees whose retirement date is within or after the period start)
  const categorizedEmployees = useMemo(() => {
    // Compute period start date from selectedMonth and closingDay
    const closing = parseInt(closingDay, 10) || 15;
    const [pYear, pMonth] = selectedMonth.split('-').map(Number);
    let periodStart = '';
    if (pYear && pMonth) {
      const prevMonth = pMonth === 1 ? 12 : pMonth - 1;
      const prevYear = pMonth === 1 ? pYear - 1 : pYear;
      periodStart = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(closing + 1).padStart(2, '0')}`;
    }

    const result: Record<'fulltime' | 'parttime', AdminEmployee[]> = { fulltime: [], parttime: [] };
    for (const emp of employees) {
      // Skip if retired before the payroll period starts (no attendance during period)
      if (emp.retirementDate && periodStart && emp.retirementDate < periodStart) continue;
      // Skip retirees without a retirement date (cannot determine period overlap)
      if (emp.status === 'retired' && !emp.retirementDate) continue;
      // Skip employment type '\u305D\u306E\u4ED6' (e.g. managers) from payroll
      if (emp.employmentType === '\u305D\u306E\u4ED6') continue;
      const cat = determineCategory(emp);
      if (cat === 'fulltime') {
        result.fulltime.push(emp);
      } else {
        result.parttime.push(emp);
      }
    }
    for (const key of Object.keys(result) as ('fulltime' | 'parttime')[]) {
      result[key] = sortEmployeesDefault(result[key]);
    }
    return result;
  }, [employees, selectedMonth, closingDay]);

  // Associate every response with its requested month; never reuse the previous month on failure.
  const fetchOverrides = useCallback(async () => {
    const sequence = ++overrideFetchSequence.current;
    const period = selectedMonth;
    setOverridesLoading(true);
    setOverridesError('');
    setOverridesPeriod('');
    setOverrides(new Map());
    try {
      if (!supabaseClient || !period) throw new Error('給与調整値を取得できません。');
      const map = await loadPayrollOverrides(supabaseClient, period, payrollSourceFingerprint);
      if (sequence !== overrideFetchSequence.current || currentPeriod.current !== period) return;
      setOverrides(map);
      setOverridesPeriod(period);
    } catch (error) {
      if (sequence === overrideFetchSequence.current && currentPeriod.current === period) {
        setOverridesError(`給与調整値の読込に失敗しました。${(error as any)?.message ?? ''}`);
      }
    } finally {
      if (sequence === overrideFetchSequence.current) setOverridesLoading(false);
    }
  }, [supabaseClient, selectedMonth, payrollSourceFingerprint]);

  useEffect(() => { fetchOverrides(); return () => { overrideFetchSequence.current++; }; }, [fetchOverrides, payrollSourceFingerprint]);

  // Helper: get employee overrides or empty map
  const empOverrides = useCallback(
    (empId: string) => overrides.get(empId) ?? new Map<string, number>(),
    [overrides]
  );

  // Calculate payroll data for each category
  const fulltimePayroll = useMemo<FulltimePayroll[]>(
    () => categorizedEmployees.fulltime.map((emp) =>
      calculateFulltimePayroll(emp, monthlyData[emp.id] ?? [], calculateOvertime, empOverrides(emp.id), payrollRuleOptions)
    ),
    [categorizedEmployees.fulltime, monthlyData, empOverrides, calculateOvertime, payrollRuleOptions]
  );

  const parttimePayroll = useMemo<ParttimePayroll[]>(
    () => categorizedEmployees.parttime.map((emp) =>
      calculateParttimePayroll(emp, monthlyData[emp.id] ?? [], calculateOvertime, empOverrides(emp.id), payrollRuleOptions)
    ),
    [categorizedEmployees.parttime, monthlyData, empOverrides, calculateOvertime, payrollRuleOptions]
  );

  // Build autoValues map for override comparison
  const buildAutoValues = useCallback(
    (emps: AdminEmployee[], category: EmployeeCategory, fields: string[]): Map<string, Map<string, number>> => {
      const result = new Map<string, Map<string, number>>();
      for (const emp of emps) {
        const records = monthlyData[emp.id] ?? [];
        const fieldMap = new Map<string, number>();
        for (const f of fields) fieldMap.set(f, getAutoValue(category, f, emp, records, calculateOvertime, payrollRuleOptions));
        result.set(emp.id, fieldMap);
      }
      return result;
    },
    [monthlyData, calculateOvertime, payrollRuleOptions]
  );

  const fulltimeAutoValues = useMemo(
    () => buildAutoValues(categorizedEmployees.fulltime, 'fulltime', fulltimeFields),
    [categorizedEmployees.fulltime, buildAutoValues, fulltimeFields]
  );
  const parttimeAutoValues = useMemo(
    () => buildAutoValues(categorizedEmployees.parttime, 'parttime', parttimeFields),
    [categorizedEmployees.parttime, buildAutoValues, parttimeFields]
  );
  // A missing unit price would silently pay 0 yen, so list those employees explicitly.
  const unsetPartAllowanceEmployees = useMemo(
    () => partSpecialAllowanceEnabled
      ? categorizedEmployees.parttime.filter(emp => getPartSpecialAllowanceUnitPrice(emp) === null)
      : [],
    [partSpecialAllowanceEnabled, categorizedEmployees.parttime]
  );

  // Do not report an optimistic value as saved: PostgREST failures are returned as `error`.
  const handleOverrideChange = useCallback(
    async (employeeId: string, fieldName: string, value: number | null) => {
      if (overrideSaveActive.current || submissionConfirmed) return;
      const period = selectedMonth;
      overrideSaveActive.current = true;
      setOverridesSaving(true);
      setOverridesError('');
      try {
        if (!supabaseClient) throw new Error('給与調整値の保存先に接続できません。');
        const result = value === null
          ? await supabaseClient.from('payroll_overrides').delete().eq('employee_id', employeeId).eq('period_key', period).eq('field_name', fieldName)
          : await supabaseClient.from('payroll_overrides').upsert({
              employee_id: employeeId, period_key: period, field_name: fieldName,
              override_value: value, updated_at: new Date().toISOString(),
            }, { onConflict: 'employee_id,period_key,field_name' });
        if (result.error) throw result.error;
        if (currentPeriod.current !== period) return;
        await fetchOverrides();
        onReloadMonthly?.();
      } catch (error) {
        if (currentPeriod.current === period) {
          setOverridesError(`給与調整値の保存を確認できませんでした。再読み込みして保存結果を確認してください。${(error as any)?.message ?? ''}`);
        }
      } finally {
        overrideSaveActive.current = false;
        setOverridesSaving(false);
      }
    },
    [supabaseClient, selectedMonth, fetchOverrides, submissionConfirmed, onReloadMonthly]
  );

  // CSV export (fulltime and parttime only)
  const handleCsvExport = useCallback(
    (category: 'fulltime' | 'parttime' | 'all') => {
      setCsvMenuOpen(false);
      if (category === 'all') {
        exportAllPayrollCsv(fulltimePayroll, parttimePayroll, selectedMonth, benefitRuleEnabled, partSpecialAllowanceEnabled);
        return;
      }
      if (category === 'fulltime') exportFulltimePayrollCsv(fulltimePayroll, selectedMonth, benefitRuleEnabled);
      if (category === 'parttime') exportParttimePayrollCsv(parttimePayroll, selectedMonth, benefitRuleEnabled, partSpecialAllowanceEnabled);
    },
    [fulltimePayroll, parttimePayroll, selectedMonth, benefitRuleEnabled, partSpecialAllowanceEnabled]
  );

  const periodLabel = useMemo(() => computePeriodLabel(selectedMonth, closingDay), [selectedMonth, closingDay]);
  const isLoading = monthlyLoading || overridesLoading || overridesPeriod !== selectedMonth;
  const laborRows = useMemo(() => {
    const rows = buildLaborRows(fulltimePayroll, parttimePayroll, monthlyData);
    const includedIds = new Set(rows.map(row => row.employeeId));
    // Preserve people omitted by the legacy payroll table as explicit, reviewed exclusions.
    for (const employee of employees.filter(item => !includedIds.has(item.id))) {
      const category = determineCategory(employee);
      const records = monthlyData[employee.id] ?? [];
      const extra = category === 'fulltime'
        ? buildLaborRows([calculateFulltimePayroll(employee, records, calculateOvertime, empOverrides(employee.id), payrollRuleOptions)], [], monthlyData)
        : buildLaborRows([], [calculateParttimePayroll(employee, records, calculateOvertime, empOverrides(employee.id), payrollRuleOptions)], monthlyData);
      rows.push({ ...extra[0], excluded: true, reviewed: false, adjustmentReason: '',
        sourceReviewNote: employee.status === 'retired' && !employee.retirementDate
          ? '退職日が未登録のため対象期間の在籍が不明です。今回は対象外か、転記する数値があるかを確認してください。'
          : employee.employmentType === 'その他'
            ? '「その他」の雇用区分です。原本の管理者等の欄への転記が必要か確認し、必要なら対象外を解除してシート区分・数値を確認してください。'
            : '給与表の自動集計から外れている職員です。対象期間の在籍・再入職・勤怠を確認し、対象外の場合は理由を記入してください。'
      });
    }
    return rows;
  }, [fulltimePayroll, parttimePayroll, monthlyData, employees, calculateOvertime, empOverrides, payrollRuleOptions]);

  // For tab counts: benefit tab shares parttime employees
  const tabCounts: Record<CategoryTab, number> = {
    fulltime: categorizedEmployees.fulltime.length,
    parttime: categorizedEmployees.parttime.length,
    benefit: categorizedEmployees.parttime.length,
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{'\u7D66\u4E0E\u8A08\u7B97'}</h2>
            <p className="text-sm text-gray-500 mt-1">{periodLabel}</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => onMonthChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={() => { fetchOverrides(); onReloadMonthly?.(); }}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title={'\u518D\u8AAD\u307F\u8FBC\u307F'}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <div className="relative" ref={csvMenuRef}>
              <button
                disabled={isLoading || !!overridesError || overridesSaving}
                onClick={() => setCsvMenuOpen((v) => !v)}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                {`CSV\u51FA\u529B`}
                <ChevronDown className="w-3 h-3" />
              </button>
              {csvMenuOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <button onClick={() => handleCsvExport('fulltime')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {'\u6B63\u8077\u54E1\u3092CSV\u51FA\u529B'}
                  </button>
                  <button onClick={() => handleCsvExport('parttime')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {'\u975E\u5E38\u52E4\u3092CSV\u51FA\u529B'}
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button onClick={() => handleCsvExport('all')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">
                    {'\u5168\u30AB\u30C6\u30B4\u30EA\u3092CSV\u51FA\u529B'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {overridesError && <p role="alert" className="mx-6 mt-4 rounded bg-red-50 p-3 text-sm text-red-800">{overridesError} 再読み込み後に確認してください。</p>}
      {unsetPartAllowanceEmployees.length > 0 && (
        <p role="status" className="mx-6 mt-4 rounded bg-amber-50 p-3 text-sm text-amber-900">
          {`${PART_SPECIAL_ALLOWANCE_LABEL}の時間単価が未設定のため0円で計算しています：${unsetPartAllowanceEmployees.map(emp => emp.name).join('、')}。従業員管理で時間単価を設定してください。`}
        </p>
      )}
      {submissionConfirmed && <p className="mx-6 mt-4 rounded bg-blue-50 p-3 text-sm text-blue-900">社労士提出値は月次確定済みです。修正する場合は下の確認画面で確定を解除してください。</p>}
      {/* Category tabs */}
      <div className="px-6 pt-4">
        <div className="flex flex-wrap gap-2">
          {CATEGORY_TABS.map(({ key, label, icon }) => {
            const count = tabCounts[key];
            const isActive = activeCategory === key;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {icon}
                {label}
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                  isActive ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table content */}
      <fieldset disabled={submissionConfirmed || overridesSaving || !!overridesError} className="min-w-0 p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-500 text-sm">{'\u8AAD\u307F\u8FBC\u307F\u4E2D...'}</span>
          </div>
        ) : (
          <>
            {activeCategory === 'fulltime' && (
              <FulltimePayrollTable data={fulltimePayroll} autoValues={fulltimeAutoValues}
                overrides={overrides} onOverrideChange={handleOverrideChange} benefitRuleEnabled={benefitRuleEnabled} />
            )}
            {activeCategory === 'parttime' && (
              <ParttimePayrollTable data={parttimePayroll} autoValues={parttimeAutoValues}
                overrides={overrides} onOverrideChange={handleOverrideChange} benefitRuleEnabled={benefitRuleEnabled}
                partSpecialAllowanceEnabled={partSpecialAllowanceEnabled} />
            )}
            {activeCategory === 'benefit' && (
              <ParttimeBenefitTable
                employees={categorizedEmployees.parttime}
                payrollData={parttimePayroll}
                autoValues={parttimeAutoValues}
                overrides={overrides}
                onOverrideChange={handleOverrideChange}
                benefitRuleEnabled={benefitRuleEnabled}
                partSpecialAllowanceEnabled={partSpecialAllowanceEnabled}
              />
            )}
          </>
        )}
      </fieldset>
      <LaborSubmissionPanel
        key={`${selectedMonth}:${closingDay}`}
        periodKey={selectedMonth} closingDay={parseInt(closingDay, 10)} client={supabaseClient}
        calculatedRows={laborRows} loading={isLoading || overridesSaving || !!overridesError}
        sourceFingerprint={payrollSourceFingerprint} sourceError={payrollSourceError || overridesError}
        onReloadMonthly={onReloadMonthly} onConfirmedChange={setSubmissionConfirmed}
      />
    </div>
  );
};

export default PayrollTab;
